import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useAssistantCaptionPacer } from "@/hooks/use-assistant-caption-pacer"
import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock"
import { useIsAppHomeRoute } from "@/hooks/use-is-app-home-route"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useTripleTap } from "@/hooks/use-triple-tap"
import type { VoiceConnectionStatus } from "@/types/voice-chat-rtc"
import { cn } from "cn"

import { BOTTOM_EDGE_AVOID } from "@/lib/bottom-edge/probe"
import { useRouter } from "@/lib/router"
import {
  canEnableDebugMode,
  selectPageSideMapVisible,
  setDebugMode,
} from "@/lib/storage/app-slice"
import { selectChatMapColumnVisible } from "@/lib/storage/chat-selectors"
import { hangUpVoiceMode } from "@/lib/storage/chat-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { selectHasAnyEvents } from "@/lib/storage/itinerary-slice"
import { selectTrip } from "@/lib/storage/trip-slice"
import { selectShowAppHomeMap } from "@/lib/storage/ui-settings-slice"
import { selectChatId, selectIsMindtripUser } from "@/lib/storage/user-slice"
import { selectVoiceOverlayCoveredBySheet } from "@/lib/storage/voice-chat-coverage"
import { setVoiceAudioElement } from "@/lib/storage/voice-chat-middleware"
import {
  selectHasVoiceTranscript,
  selectVisibleVoiceToolCalls,
  selectVoiceConnecting,
  selectVoiceConnectionStatus,
  selectVoiceErrorMessage,
  selectVoiceModeChatId,
  selectVoiceModeMinimized,
  selectVoiceModeOpen,
  selectVoiceMuted,
  selectVoicePausable,
  selectVoicePaused,
  voiceChatActions,
} from "@/lib/storage/voice-chat-slice"
import { isChatPage, isTripPageUrl } from "@/lib/utils/page"
import {
  orbFlight,
  orbRise,
  readDockOrbRect,
  whenDockOrbArrives,
} from "@/lib/voice-orb-handoff"
import { ChatTripButton } from "@/components/chat-trip-button"
import IconChevronDown from "@/components/icons/chevron-down"
import IconCross from "@/components/icons/cross"
import { LazyVoiceChatContent } from "@/components/lazy-voice-chat-content"
import { LazyVoiceChatPlaces } from "@/components/lazy-voice-chat-places"
import { LazyVoiceChatToolCalls } from "@/components/lazy-voice-chat-tool-calls"
import { IconButton } from "@/components/ui/icon-button"
import { VoiceChatOrb } from "@/components/voice-chat-orb"
import { VoiceCallControls } from "@/components/voice-chat/voice-call-controls"
import { VoiceMiniPlayer } from "@/components/voice-mini-player"

const statusLabels: Record<VoiceConnectionStatus, string> = {
  idle: "Getting ready…",
  connecting: "Getting ready…",
  reconnecting: "Connecting…",
  listening: "Listening…",
  assistant_speaking: "Mindtrip is speaking…",
  error: "Something went wrong",
}

// Where the caption band (the live transcript) exists: desktop widths, where
// the overlay has room for the words and a call is as much watched as
// listened to. A phone keeps the orb and the cards, which are most of a small
// screen already. THE ONE LINE TO CHANGE — any media query works: `"all"` for
// everyone, `"not all"` for no one.
export const CAPTIONS_MEDIA = MEDIA_QUERIES.lg

// Root-mounted voice-mode host: everything voice needs to outlive the chat page
// it started on. Always mounted (self-gates on Redux), so it must stay cheap
// while closed.
export function VoiceChat() {
  return (
    <>
      {/* The one audio sink for every session, and a sibling of the gated tree
        rather than part of it: the middleware claims it via setVoiceAudioElement
        and keeps playing through the close, so an element that unmounted with
        the surfaces would cut the tail off the last response. Mounted for the
        app's lifetime, it can't.
        Deliberately NOT `autoPlay`: this element exists on every page for every
        visitor, so an autoplay policy declining it fires against sessions that
        will never happen. The RTC starts playback itself when a remote track
        actually arrives, where the failure is a real one and gets handled. */}
      <audio ref={setVoiceAudioElement} className="hidden" />
      <VoiceChatSurfaces />
    </>
  )
}

// The visible half. A live session is bound to the page it started on — a chat
// page, a trip, the app home: the middleware owns the session lifecycle off
// voiceModeOpened/voiceModeClosed, this host no longer ends the session on
// unmount, and leaving the call's page ends it (the middleware closes on the
// chat-id change, and on any pathname-changing navigation via
// setPreviousPage). So voice only ever presents on its origin page.
//
// Presentation there is derived, not stored:
//   !open / off the origin page       → nothing (off-origin the session is ending)
//   open, on the origin page          → full takeover overlay
//   open, on origin but sheet-covered → full overlay stays mounted UNDER the
//                                        sheet; mini-player rides on top
//   open, but put down by the user    → mini-player alone; the overlay LEAVES
// Closing the covering sheet re-satisfies the full branch with the same mounted
// tree, so restore is free. A collapse is the one case that really unmounts —
// the point is to hand the page back, and the overlay holds a body scroll lock
// while mounted — so coming back replays the entry fade, which is right: it is
// a re-presentation, not a restore.
function VoiceChatSurfaces() {
  const open = useAppSelector(selectVoiceModeOpen)
  const voiceChatId = useAppSelector(selectVoiceModeChatId)
  // The chat the user is currently viewing (on a non-chat page, the chat the
  // open flow just created — createChat dispatches setChat). resetChat() on
  // chat→non-chat nav and setChat() on chat→chat nav keep this in step with
  // the route, and off chat routes nothing disturbs it while the call lives,
  // so an equality check against the call's chat is a reliable "am I on the
  // call's page?" gate.
  const currentChatId = useAppSelector(selectChatId)

  if (!open || voiceChatId === null) {
    return null
  }

  // Off the origin chat the session is being torn down (the middleware ends a
  // call whose chat you navigate away from), so render nothing there — the
  // overlay belongs to one chat and must never paint over a different one.
  if (currentChatId !== voiceChatId) {
    return null
  }

  return <VoiceChatPresentation />
}

// Split from the gate above so that this state's lifetime IS the session's.
//
// Held one level up, beside the gates, its hooks would run before their early
// returns and the state would outlive the call it describes: ending a collapsed
// call leaves `returning` standing, and the NEXT call then opens by flying its
// orb in from wherever the last one's dock had been. Measured that way round —
// a call that had never been docked started at the dock chip's position, bottom
// left, and travelled up.
function VoiceChatPresentation() {
  // A sheet opening from inside voice mode (the place carousel's cards) would
  // otherwise open *under* the overlay — invisible, and unreachable on mobile
  // where the sheet is a full-screen takeover. Yield the surface instead.
  const coveredBySheet = useAppSelector(selectVoiceOverlayCoveredBySheet)
  const minimized = useAppSelector(selectVoiceModeMinimized)
  const overlay = useOverlayPresence(minimized)
  // A call that opened minimized never showed the overlay, so its first dock
  // has no orb flying in to time against; it fades in like a covered one.
  const [overlayShown, setOverlayShown] = useState(!minimized)
  if (!minimized && !overlayShown) {
    setOverlayShown(true)
  }

  return (
    <>
      {/* Runs in every presentation so captions keep advancing even while the
        heavy content chunk is occluded under a covering sheet. Isolated in a
        null component so its per-tick selector reads don't re-render the overlay
        tree. */}
      <VoiceSessionPacer />
      {/* The full overlay stays mounted even while a sheet covers it: the sheet
        just paints on top (it renders after <main>, so it outranks the
        overlay's z-3). Closing the sheet reveals the SAME tree — no remount, no
        replayed entry animation, no captions re-revealing. The mini-player
        rides above the sheet only while covered, to keep controls reachable. */}
      {overlay.present && (
        <VoiceChatOverlay
          collapsing={overlay.collapsing}
          returning={overlay.returning}
        />
      )}
      {(minimized || coveredBySheet) && (
        // Why the dock is appearing decides how fast it does. Arriving over a
        // sheet it waits for the sheet to land first; arriving from a collapse
        // there is nothing to wait for, and waiting is the whole defect.
        <VoiceMiniPlayer
          entry={minimized && overlayShown ? "handoff" : "coverage"}
        />
      )}
    </>
  )
}

// How long the hand-off takes. The overlay cannot leave the instant it is asked
// to — there has to still be a surface to hand the call to — so the stored flag
// flips at once and the mount lags it by exactly this.
//
// 300ms is the orb's flight, and every other part of the exit finishes inside
// it: the chrome goes in 110ms, the ground behind it over 190ms from +70, and
// the dock fades up over the orb's exchange window alone (ENTRY_TIMING in
// voice-mini-player.tsx). Two thirds of the drawer duration, because the app's
// drawer curve spends most of its travel in the first third — at 500ms the
// motion is visibly over long before the mount is.
export const HANDOFF_MS = 300

// Carries the overlay's orb onto the dock's, and back off it.
//
// The orb is the only thing the two surfaces genuinely share, so it is the only
// thing that travels: moving it states that the call went down there, where a
// cross-fade only says both existed. Everything else clears out of its way.
//
// Only the OVERLAY's orb ever moves, in both directions — the dock's card clips
// its contents (a hard-edged halo crossing a straight edge shows a flat chord),
// so an orb flying into it would be invisible until it arrived.
//
// The two overlap at the end rather than swapping, because a scaled-down
// `compact` orb is not a `mini` orb: the traveller fades out over its last
// stretch while the dock's chip fades up at the same centre, where nothing is
// moving any more. The dock is in front during that exchange, not behind it —
// it paints above this overlay whatever the z-indexes say, which is why the
// window is the only place it may appear (see ENTRY_TIMING).
function useVoiceOrbHandoff(
  orbRef: RefObject<HTMLElement | null>,
  { collapsing, returning }: { collapsing: boolean; returning: boolean }
) {
  // Before paint: this measures the orb where the layout has just put it and
  // animates from somewhere else, so a deferred effect would show one frame at
  // the destination first.
  useLayoutEffect(() => {
    const node = orbRef.current
    if (!node) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    // Three ways an orb can arrive or leave, separated by what each of them can
    // ask the dock. A call STARTING has no dock in its past or its present, so
    // it asks nothing and rises from the bottom of the window. RETURNING, the
    // dock has just gone — by construction, since coming back is what unmounts
    // it — and the rect it left behind is exactly what the orb flies out of.
    // COLLAPSING, the dock does not exist yet (it is behind a Portal, which
    // renders nothing until its own layout effect has run), and the rect
    // standing from last time is about a dock that is no longer there. So that
    // one waits.
    if (!collapsing && !returning) return startRise(node)
    if (returning) return startFlight(node, collapsing)

    let cancel = () => {}
    const stop = whenDockOrbArrives(() => {
      cancel = startFlight(node, collapsing)
    })
    return () => {
      stop()
      cancel()
    }
  }, [collapsing, returning, orbRef])
}

// Read the app's own drawer curve rather than restating it, so nothing that
// travels here can drift from every other surface that travels.
function drawerEase() {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue("--ease-drawer")
      .trim() || "cubic-bezier(0.32, 0.72, 0, 1)"
  )
}

// One animation, where the hand-off needs two: there is nothing at the far end
// to exchange with, so the orb simply arrives. The root's own 300ms entry fade
// runs underneath it, on the same beat.
function startRise(orb: HTMLElement) {
  const rise = orbRise(orb.getBoundingClientRect(), window.innerHeight)
  if (!rise) return () => {}
  const below = `translate(0px, ${rise.y}px) scale(${rise.scale})`
  const travel = orb.animate([{ transform: below }, { transform: "none" }], {
    duration: HANDOFF_MS,
    easing: drawerEase(),
    fill: "both",
  })
  return () => travel.cancel()
}

function startFlight(orb: HTMLElement, collapsing: boolean) {
  const dock = readDockOrbRect()
  if (!dock) return () => {}
  const flight = orbFlight(orb.getBoundingClientRect(), dock)
  if (!flight) return () => {}

  const ease = drawerEase()
  const away = `translate(${flight.x}px, ${flight.y}px) scale(${flight.scale})`

  // Two animations, not one set of keyframes: the travel and the exchange want
  // different curves, and the exchange is a sliver at the end of the travel
  // rather than a phase of it.
  const travel = orb.animate(
    collapsing
      ? [{ transform: "none" }, { transform: away }]
      : [{ transform: away }, { transform: "none" }],
    { duration: HANDOFF_MS, easing: ease, fill: "both" }
  )
  const exchange = orb.animate(
    collapsing
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [{ opacity: 0 }, { opacity: 1 }],
    {
      duration: ORB_EXCHANGE_MS,
      delay: collapsing ? HANDOFF_MS - ORB_EXCHANGE_MS : 0,
      easing: "linear",
      fill: "both",
    }
  )

  return () => {
    travel.cancel()
    exchange.cancel()
  }
}

// The sliver at the end of the flight where the traveller hands over to the
// dock's own chip. Both are at the same centre by then — the drawer curve has
// spent nearly all its travel — so the swap has nothing left to give away.
//
// It is also the only window the dock may paint in, for a reason that is not
// about timing at all: the dock always paints ABOVE this overlay, so these are
// the only frames where its being on top is invisible. See ENTRY_TIMING.
const ORB_EXCHANGE_MS = 80

// Everything except the orb clears first and fast, so there is only ever one
// thing to follow down. Gone well before the ground behind it starts to go.
const CHROME_EXIT =
  "motion-safe:animate-out motion-safe:fade-out motion-safe:duration-[110ms] motion-safe:fill-mode-forwards"

// Whether the overlay is on screen, and whether it is on its way out. Derived
// from the stored flag, but lagging it: `collapsing` is the window in which the
// exit runs, and it is the only time both surfaces are mounted at once by the
// user's own doing.
//
// Exported for its own tests. The component around it cannot be driven through
// a second session in jsdom — reopening needs voiceModeOpened, which sends the
// middleware after a microphone that is not there — but this takes a boolean
// and a timer and nothing else.
export function useOverlayPresence(minimized: boolean) {
  // A call that opens minimized has no overlay to exit.
  const [exited, setExited] = useState(minimized)
  const [returning, setReturning] = useState(false)
  const [lastMinimized, setLastMinimized] = useState(minimized)

  // Adjusted during render rather than synced by an effect: coming back cancels
  // an exit outright, and that answer is available the moment the flag changes.
  // Going out starts a fresh one.
  if (lastMinimized !== minimized) {
    setLastMinimized(minimized)
    // Only a call that actually left is coming back. Tapping return partway
    // through the exit never unmounted the overlay, so there is nothing to
    // fly in from — cancelling the exit is the whole of it.
    setReturning(!minimized && exited)
    setExited(false)
  }

  const collapsing = minimized && !exited

  useEffect(() => {
    if (!collapsing) return
    // A timer is the one part of this that genuinely lives outside render.
    // Under a reduced-motion preference there is no exit to wait for, so the
    // same timer simply has nothing to wait: the state change still has to
    // happen, and it still has to happen asynchronously.
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
    const timer = window.setTimeout(
      () => setExited(true),
      reduced ? 0 : HANDOFF_MS
    )
    return () => window.clearTimeout(timer)
  }, [collapsing])

  return { present: !minimized || !exited, collapsing, returning }
}

// Single owner of the caption reveal pacing for the whole session, regardless of
// which surface is visible.
function VoiceSessionPacer() {
  useAssistantCaptionPacer()
  return null
}

// The full voice takeover. Covers the content column only, on whatever page
// the call started: positioning mirrors ChatMapArea (fixed, offset by
// --nav-width, all :root custom properties so it resolves at the root mount),
// taking the LEFT half from the `split` breakpoint up so the right half (the
// map on chat and trip pages) stays visible — except on /home with its map
// closed, where there is no right half to keep and it spans the column; below
// `sm` it is a full-screen takeover. Purely presentational — the middleware
// owns the session.
function VoiceChatOverlay({
  collapsing,
  returning,
}: {
  collapsing: boolean
  returning: boolean
}) {
  const dispatch = useAppDispatch()
  // The wrapper is the orb's own box — the only other child is sr-only, which
  // is taken out of flow — so moving it moves exactly the orb.
  const orbRef = useRef<HTMLDivElement>(null)
  useVoiceOrbHandoff(orbRef, { collapsing, returning })

  // The other half of the dock's own focus move: picking the call back up takes
  // the dock off the screen, and without this focus falls to <body> again. The
  // collapse control is the counterpart of the button that was just pressed.
  const collapseRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (returning) collapseRef.current?.focus()
  }, [returning])
  const isMindtripUser = useAppSelector(selectIsMindtripUser)
  // Narrow subscriptions: the shell re-renders only when one of these changes,
  // not on the reveal stream (paced per tick) or the orb's ~25Hz levels. The
  // caption/places/orb subtrees own their own reads.
  const status = useAppSelector(selectVoiceConnectionStatus)
  const connecting = useAppSelector(selectVoiceConnecting)
  const muted = useAppSelector(selectVoiceMuted)
  const paused = useAppSelector(selectVoicePaused)
  const pausable = useAppSelector(selectVoicePausable)
  const errorMessage = useAppSelector(selectVoiceErrorMessage)
  const hasTranscript = useAppSelector(selectHasVoiceTranscript)
  // Presentation only: the width of the screen decides, and nothing else.
  const showTranscript = useMediaQuery(CAPTIONS_MEDIA)
  // The call's trip: voice only presents while the current chat is the call's
  // chat, so the chat's trip is the one the assistant is editing.
  const trip = useAppSelector(selectTrip)
  const hasEvents = useAppSelector(selectHasAnyEvents)
  const isAppHome = useIsAppHomeRoute()
  const showAppHomeMap = useAppSelector(selectShowAppHomeMap)
  const chatMapColumnVisible = useAppSelector(selectChatMapColumnVisible)
  const pageSideMapVisible = useAppSelector(selectPageSideMapVisible)
  const router = useRouter()
  // Chat and trip pages keep their header visible and leave the chat map's
  // column. App home follows its own map toggle. Every other page is covered
  // to the top, leaving only a map the page shows on its right half
  // (usePageSideMap).
  const chatLayout = isChatPage(router) || isTripPageUrl(router.asPath)
  let leavesMapVisible = pageSideMapVisible
  if (isAppHome) {
    leavesMapVisible = showAppHomeMap
  } else if (chatLayout) {
    leavesMapVisible = chatMapColumnVisible
  }
  // The status label is the orb's spoken half (screen readers only), so it
  // ends on the same beat the connect treatment does: while that runs, a
  // status claiming the session is live is premature and reads back as the
  // connect copy instead.
  const statusLabel =
    connecting && (status === "listening" || status === "assistant_speaking")
      ? statusLabels.connecting
      : statusLabels[status]
  // Tool calls count as content while they are visible, including after the
  // readout has started its exit fade, so the orb does not pump back to its
  // medium pose between a completed call and the answer's first caption.
  const hasToolCalls = useAppSelector(
    (state) => selectVisibleVoiceToolCalls(state).length > 0
  )
  // Gated on being the visible (full) surface by where this lives: the headless
  // / off-origin session never mounts this component, so it can't lock scroll
  // globally. Held even while a sheet covers the overlay — the overlay is still
  // mounted under it, and the sheet's own lock is the one on top.
  useBodyScrollLock()

  const hasContent = hasTranscript || hasToolCalls
  const isError = status === "error"

  const handleClose = () => {
    void dispatch(hangUpVoiceMode())
  }

  const debugTap = useTripleTap(() => {
    if (canEnableDebugMode(isMindtripUser)) {
      dispatch(setDebugMode(true))
    }
  })

  return (
    <div
      {...debugTap}
      // Nothing in here is reachable once it is leaving. The exit fades the
      // chrome out over 110ms and then holds it transparent for the rest of the
      // hand-off, but opacity has never affected hit-testing: for those 300ms
      // the overlay stayed a full-screen sheet of invisible, live controls over
      // the page it had just handed back. A tap meant for the page landed on
      // it, and the bottom control row sits exactly where a phone's composer
      // does — so the tap that ended the call was one row from the tap that
      // muted it. `inert` rather than pointer-events-none because the same
      // applies to the keyboard and to a screen reader: a surface on its way
      // out should not be tabbable or announced either.
      inert={collapsing}
      // Seen through by the bottom-edge probe, so --bottom-inset keeps
      // describing the page underneath. Counted as cover, it zeroed the inset
      // for the whole call: a collapse then docked the mini-player at the
      // bare edge, flew the orb there, and slid the card up once the overlay
      // left and the composer reappeared.
      data-bottom-edge={BOTTOM_EDGE_AVOID}
      className={cn(
        // Renders in place, inside <main>'s stacking context: z-3 puts it over
        // every piece of page chrome (map z-0, composer and mobile nav z-1,
        // header z-2) while <main>'s own `isolate` keeps it under everything
        // the layout renders after it — sheets, dialogs, toasts. So a place
        // card in the voice carousel opens its sheet ON TOP of the overlay
        // without the overlay having to know the sheet layer exists. When a
        // sheet fully covers it, voice collapses to the mini-player, which is
        // the always-on-top persist surface (portaled to <body>, above the
        // sheet layer). See selectVoiceOverlayCoveredBySheet.
        "fixed z-3 flex flex-col",
        // Mobile: true fullscreen takeover — spans the viewport so it paints
        // over the header and bottom nav. From sm up it returns to covering just
        // the content column, offset by the header/nav chrome so the map stays
        // visible in split.
        "inset-0 sm:bottom-(--bottom-nav-offset,0px)",
        // Only chat and trip pages keep their header visible during a call.
        chatLayout && "sm:top-(--content-top)",
        "sm:left-(--nav-width)",
        leavesMapVisible &&
          "split:right-[calc(50%-var(--nav-width)/2)] split:rounded-r-2xl",
        !collapsing &&
          "motion-safe:animate-in motion-safe:duration-300 motion-safe:fade-in"
      )}
    >
      {/* The ground, on its own layer rather than on the root. Going out it is
        the last thing to leave — it holds while the chrome clears, then
        dissolves to reveal the chat — and the orb is still crossing it when it
        does. A child cannot be more opaque than its parent, so the background
        the orb flies over must not be the box the orb lives in.

        Negative z within the root's own stacking context (it is positioned, at
        z-3), so it sits under the in-flow content it has always been the
        backdrop of, and no further back in the page than it ever was. */}
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 -z-10 bg-background",
          collapsing &&
            "motion-safe:animate-out motion-safe:delay-[70ms] motion-safe:duration-[190ms] motion-safe:fill-mode-forwards motion-safe:fade-out"
        )}
      />
      {/* Floated, not in-flow: the corner chrome shouldn't cost the centered
        column vertical space. */}
      <div
        className={cn(
          "absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-3 p-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] sm:pt-3",
          collapsing && CHROME_EXIT
        )}
      >
        {/* The two things that can be done to this surface, kept together: end
          the call, or put it down. The adjacency is what keeps the X readable
          — this change is what first makes "hide" a thing the overlay can do,
          and a lone X with a chevron in the opposite corner invites being read
          as the hiding one. */}
        <div className="flex items-start gap-1">
          <IconButton
            variant="ghost"
            size="sm"
            icon={IconCross}
            label="Close voice mode"
            onClick={handleClose}
          />
          {/* The session runs on; only the surface changes. Coming back is the
            mini-player's own body tap, which clears this and any covering
            sheet in one go (restoreVoiceOverlay). */}
          <IconButton
            ref={collapseRef}
            variant="ghost"
            size="sm"
            icon={IconChevronDown}
            label="Collapse voice mode"
            onClick={() => dispatch(voiceChatActions.voiceModeCollapsed())}
          />
        </div>
        {/* The door to the trip the call is editing — only while the overlay
          is a fullscreen takeover. From sm up it starts below the page header
          (top-(--content-top) on the root), so the page's own trip button is
          still on screen and this would be a second door to the same sheet.
          Tapping opens the trip sheet OVER the overlay (the sheet layer sits
          above it, see the root className): it covers voice, the mini-player
          takes over, and the session runs on — a same-chat sheet never changes
          the chat id. Only for a trip the user actually has: every chat
          carries one from its first decoded intent, and the button's other
          state invites you to stop and fill in the create-trip dialog, which
          is not what the corner of a call is for. Same test the button makes
          to choose that state, so the two never disagree. */}
        {trip && (trip.user_created || hasEvents) && (
          <ChatTripButton trip={trip} className="sm:hidden" />
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-6">
        {/* Two columns, chosen by the one media query and nothing else.

          From desktop widths the column is the one it has always been —
          orb over caption over carousel — untouched by the phone work below.
          Below them there is no caption at all, so the column is rebuilt
          around its absence rather than rendered with a hole in it. */}
        {showTranscript ? (
          <>
            {/* Top region holds the orb, bottom-aligned. Its footprint is a
              fixed fraction of the surface no matter what's been said, so the
              orb keeps one spot for the whole session — it never travels from
              a centered "welcome" pose to a docked one, and content appearing
              below can't shove it. Weighted lighter than the bottom region so
              the orb sits a little above center rather than dead-center (which
              read as too low) — but only a touch lighter (7:8, up from 3:4):
              with the caption window grown to reach the carousel, the higher
              orb left the surface top-heavy, so the whole ensemble sits
              slightly lower. */}
            <div className="flex flex-7 flex-col items-center justify-end pb-6 motion-safe:animate-in motion-safe:duration-500 motion-safe:fade-in">
              {/* Medium for the connect hero moment, easing down to
                compact once the first caption or tool chip claims the region
                below. The regions are fixed, so only the footprint animates —
                the orb never travels. */}
              <div ref={orbRef} className="relative">
                <VoiceChatOrb
                  status={status}
                  size={hasContent || isError ? "compact" : "full"}
                />
                {/* Screen-reader only: the orb's gray connecting treatment
                  (which covers reconnecting too) is the visual status; sighted
                  users never need the words. */}
                <p className="sr-only" role="status" aria-live="polite">
                  {statusLabel}
                </p>
              </div>
            </div>

            {/* Bottom region holds the caption + places (or the error): the
              caption band fills it down to the carousel pinned at its bottom
              edge, and a long answer scrolls within the band rather than
              pushing the orb up. Always present (even empty) so the split —
              and thus the orb — stays put.
              overflow-hidden, not auto: the caption band is region-sized and
              scrolls internally, so a scrollable region here only ever doubles
              up its scrollbar; clipping x also eats the place carousel's
              bleed, which would otherwise grow a horizontal scrollbar at full
              width. The negative margin / padding pair widens the clip box by
              exactly that bleed — the full px-6 on phones, where the carousel
              runs edge to edge, and the 1rem fade zone from sm up — so the
              clip never swallows the carousel's gutter while the caption
              wrapper (which spans this whole region) keeps its edges. */}
            <div
              className={cn(
                "-mx-6 flex min-h-0 flex-8 flex-col items-center overflow-hidden px-6 sm:-mx-4 sm:px-4",
                collapsing && CHROME_EXIT
              )}
            >
              {isError ? (
                // Calm, not alarming: the terminal state is a fact about the
                // connection, not a fault to paint red (mic copy included — it
                // is actionable, not an alarm).
                <p className="max-w-md text-center text-muted">
                  {errorMessage ?? statusLabels.error}
                </p>
              ) : hasTranscript ? (
                <LazyVoiceChatContent status={status} />
              ) : null}
            </div>
          </>
        ) : (
          <>
            {/* The stage. With no caption to make room for, the orb has
              everything above the carousel and sits three fifths of the way
              down it — below centre, nearer the cards it goes with, without
              crowding them. A 3:2 split of the stage's own HEIGHT, which
              padding (resolved against width) could not do.

              `transition-none` because `duration-500` is for the entry fade
              but also sets a transition duration, and with nothing naming a
              property that is `all`. */}
            <div className="grid min-h-0 flex-1 grid-rows-[3fr_auto_2fr] items-center justify-items-center transition-none motion-safe:animate-in motion-safe:duration-500 motion-safe:fade-in">
              <div ref={orbRef} className="relative row-start-2">
                {/* Full for the whole call: with nothing written underneath,
                  there is nothing for the orb to step back for, and it is
                  most of what this surface has. */}
                <VoiceChatOrb
                  status={status}
                  size={isError ? "compact" : "full"}
                />
                <p className="sr-only" role="status" aria-live="polite">
                  {statusLabel}
                </p>
              </div>
              {isError && (
                // Under the orb, which is still in the middle of the stage
                // when the line goes. Calm, not alarming: the terminal state
                // is a fact about the connection, not a fault to paint red.
                <p className="row-start-3 mt-6 max-w-md self-start text-center text-muted">
                  {errorMessage ?? statusLabels.error}
                </p>
              )}
            </div>

            {/* The carousel, at the foot of the column right above the
              readout. Its column is the caption's elsewhere (max-w-lg,
              centered) so the cards sit where they always have. The bleed
              handling is the desktop region's, for the same reason: clipping
              x eats the strip's own bleed, and the negative margin / padding
              pair widens the clip box by exactly that much.

              The height is the REGION's, held for the whole call rather than
              arriving with the strip. The strip mounts on the first
              transcript — not on the first card, which is why this looked like
              it fired "once we get talking" — and its 124px came out of the
              stage above in one frame, lifting the orb by three fifths of that
              (measured: 434 → 360). Reserved here instead, so the column has
              one shape from the first frame to the last, error pose included.
              The same box as CarouselHeightReservation in
              voice-chat-places.tsx, stated from the outside: importing it
              would pull the lazy chunk into the eager overlay, so the two
              carry the number separately. */}
            <div
              className={cn(
                "-mx-6 flex min-h-31 shrink-0 flex-col items-center overflow-hidden px-6 sm:-mx-4 sm:min-h-30 sm:px-4",
                collapsing && CHROME_EXIT
              )}
            >
              {hasTranscript && !isError && (
                <div className="w-full max-w-lg">
                  <LazyVoiceChatPlaces />
                </div>
              )}
            </div>
          </>
        )}

        {/* The tool-call readout rides just above the session controls,
          outside the squeezable regions, so search progress has one fixed
          home whether or not a caption is up. The readout self-gates on
          visible calls (null otherwise), so the line's space is reserved
          HERE, where it can hold through all of that component's absences —
          no calls yet, the lazy chunk still loading, even the error state —
          or the orb and caption regions would resize around every call.
          min-h rather than h so debug mode's full playground cards (variable
          height, Mindtrip-only) can still grow past one line. mt-3 holds the
          carousel's cards clear of the readout; the controls' top padding
          below is thinned to pay for it. */}
        <div
          className={cn(
            "mt-3 min-h-6 shrink-0",
            // Room between the cards and the controls where nothing else
            // (a caption's column, a wider screen's padding) provides it.
            !showTranscript && "mb-6",
            collapsing && CHROME_EXIT
          )}
        >
          {!isError && <LazyVoiceChatToolCalls />}
        </div>
      </div>

      <VoiceCallControls
        className={cn(
          "shrink-0 px-6 pt-3 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:pb-8",
          collapsing && CHROME_EXIT
        )}
        paused={paused}
        muted={muted}
        pausable={pausable}
        onPauseToggle={() => dispatch(voiceChatActions.pausedToggled())}
        onMuteToggle={() => dispatch(voiceChatActions.mutedToggled())}
        onEnd={handleClose}
        endTooltip="End voice chat"
      />
    </div>
  )
}
